/* Lijst-view — compacte tabel met inline CRUD, pool-badge en contactlinks. */
import React from "react";
import i18n from "i18next";
import { Check, Edit3, Mail, Trash2 } from "lucide-react";
import CandidateAvatar from "@/components/CandidateAvatar";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import { PoolBadge } from "@/components/recruit/PoolBadge";

export default function CandidatesTable({ candidates, picked, poolMap, onToggle, onOpen, onEdit, onDelete }) {
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] overflow-hidden" data-testid="candidates-table">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-white/8 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                            <th className="py-2 px-3 w-8"></th>
                            <th className="py-2 px-3 text-left">{i18n.t("search.th_name")}</th>
                            <th className="py-2 px-3 text-left">{i18n.t("search.th_title")}</th>
                            <th className="py-2 px-3 text-left">{i18n.t("search.th_company")}</th>
                            <th className="py-2 px-3 text-left">{i18n.t("search.th_location")}</th>
                            <th className="py-2 px-3 text-left">Pool</th>
                            <th className="py-2 px-3 text-left">Skills</th>
                            <th className="py-2 px-3 text-left">{i18n.t("search.th_language")}</th>
                            <th className="py-2 px-3 text-left">Contact</th>
                            <th className="py-2 px-3 text-right">Acties</th>
                        </tr>
                    </thead>
                    <tbody>
                        {candidates.map((c) => (
                            <tr key={c.id} className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors ${picked.has(c.id) ? "bg-[var(--jr-amber)]/[0.06]" : ""}`}
                                data-testid={`candidate-row-${c.id}`}>
                                <td className="py-2 px-3">
                                    <button onClick={() => onToggle(c.id)} data-testid={`row-pick-${c.id}`}
                                        className={`w-4 h-4 rounded border grid place-items-center transition-colors ${picked.has(c.id) ? "bg-[var(--jr-amber)] border-[var(--jr-amber)]" : "border-white/20 hover:border-white/40"}`}>
                                        {picked.has(c.id) && <Check size={10} className="text-black"/>}
                                    </button>
                                </td>
                                <td className="py-2 px-3">
                                    <button onClick={() => onOpen(c)} data-testid={`row-open-${c.id}`}
                                        className="flex items-center gap-2 text-left hover:text-white group">
                                        <CandidateAvatar name={c.name} url={c.photo_url} size={28} testid={`row-avatar-${c.id}`}/>
                                        <span className="text-white font-medium group-hover:underline">{c.name}</span>
                                    </button>
                                </td>
                                <td className="py-2 px-3 text-[var(--jr-text-2)] max-w-[200px] truncate" title={c.title}>{c.title}</td>
                                <td className="py-2 px-3 text-[var(--jr-text-2)] max-w-[180px] truncate">{c.current_company || "—"}</td>
                                <td className="py-2 px-3 text-[var(--jr-text-2)] max-w-[140px] truncate">{c.location || "—"}</td>
                                <td className="py-2 px-3">
                                    <PoolBadge poolKey={c.talent_pool} poolName={poolMap?.[c.talent_pool || "external"]} testid={`row-pool-${c.id}`} />
                                </td>
                                <td className="py-2 px-3">
                                    <div className="flex flex-wrap gap-0.5 max-w-[220px]">
                                        {(c.skills || []).slice(0, 3).map((s, i) => (
                                            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--jr-text-2)] truncate">{s}</span>
                                        ))}
                                        {(c.skills || []).length > 3 && (
                                            <span className="text-[9px] text-[var(--jr-text-3)] font-mono">+{(c.skills || []).length - 3}</span>
                                        )}
                                    </div>
                                </td>
                                <td className="py-2 px-3">
                                    {c.contact_language ? (
                                        <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-brand)]/10 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30" title={`${c.language_source || "auto"} · ${c.language_confidence || 0}%`}>
                                            {c.contact_language}
                                        </span>
                                    ) : <span className="text-[9px] text-[var(--jr-text-3)]">—</span>}
                                </td>
                                <td className="py-2 px-3">
                                    <div className="flex items-center gap-1">
                                        <LinkedInButton url={c.linkedin_url} name={c.name} variant="icon" className="!w-5 !h-5" testid={`table-linkedin-${c.id}`} resolve={{ type: "candidate", id: c.id }} />
                                        {c.email && (
                                            <a href={`mailto:${c.email}`} className="text-[var(--jr-info)] hover:text-white" title={c.email}>
                                                <Mail size={12}/>
                                            </a>
                                        )}
                                    </div>
                                </td>
                                <td className="py-2 px-3">
                                    <div className="flex items-center justify-end gap-0.5">
                                        <button onClick={() => onEdit(c)} data-testid={`row-edit-${c.id}`}
                                            className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/8" title={i18n.t("common.edit")}>
                                            <Edit3 size={12}/>
                                        </button>
                                        <button onClick={() => onDelete(c)} data-testid={`row-delete-${c.id}`}
                                            className="p-1.5 rounded text-[var(--jr-text-3)] hover:text-red-400 hover:bg-red-500/10" title={i18n.t("common.delete")}>
                                            <Trash2 size={12}/>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
